import { useApi } from '@hooks/useApi';
import { MerchantUser, ApiError } from '@root/globalTypes';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { queryKeys } from '@utils/consts';
import { isFailure } from '@utils/apiHelpers';
import { CreateMerchantUserData } from '@services/types';

type CreateUserParams = {
  merchantId: string;
  callback: (data: MerchantUser) => void;
};

export function useCreateMerchantUser({ merchantId, callback }: CreateUserParams) {
  const { query } = useApi();
  const queryClient = useQueryClient();
  const [apiError, setApiError] = useState<ApiError | null>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: (userData: CreateMerchantUserData): Promise<MerchantUser | ApiError> => {
      return query({
        path: `v1/staff/merchants/${merchantId}/users`,
        data: userData,
        opt: {
          method: 'POST',
        },
      });
    },
    onSettled: (data) => {
      if (!data) return;
      if (isFailure(data)) {
        setApiError(data);
      } else {
        callback(data);
      }
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: [...queryKeys.merchantUsers, merchantId] });
    },
  });

  return {
    mutate,
    isPending,
    error: apiError,
  };
}
